/**
 * Redux Slice for Orthanc Duplication System
 * Manages state for complete server duplication
 */

import { createSlice, createSelector } from '@reduxjs/toolkit';

import { readPersistedAccess } from '../core/fileSystemManager';

// Read once, at module load: the store is created once and the flag only ever
// changes through this slice's own reducers afterwards.
const persisted = readPersistedAccess();

// One metrics refresh per second at most. This only bounds re-renders — the ETA is
// what makes the progress modal re-render — it frames no measurement window.
const METRICS_SAMPLE_INTERVAL = 1000; // ms

/**
 * Refresh speed (files/sec) and ETA (seconds) from the run's cumulative average.
 * Cumulative rather than instantaneous: a DICOM download arrives in bursts, and any
 * short window read those bursts as speed swings the ETA then echoed back.
 * Speed reads realFilesCompleted only: the slots a failure branch pays back in bulk
 * must move the bar without inflating the speed.
 */
const recomputeMetrics = state => {
  if (!state.startTime) return;

  const now = Date.now();
  if (
    state.lastMetricsAt &&
    now - state.lastMetricsAt < METRICS_SAMPLE_INTERVAL
  ) {
    return;
  }

  const elapsed = (now - state.startTime) / 1000;
  if (elapsed <= 0 || state.realFilesCompleted <= 0) return;

  const { filesCompleted, filesTotal } = state.downloadProgress;
  state.lastMetricsAt = now;
  state.downloadSpeed = state.realFilesCompleted / elapsed;
  state.estimatedTimeRemaining =
    Math.max(0, filesTotal - filesCompleted) / state.downloadSpeed;
};

const initialState = {
  // Overall status
  status: 'idle', // 'idle' | 'running' | 'paused' | 'completed' | 'error'
  currentPhase: null, // 'access' | 'inventory' | 'downloading'
  hasDirectoryAccess: persisted.hasDirectoryAccess,
  directoryName: persisted.directoryName,

  // Progress tracking
  downloadProgress: {
    filesCompleted: 0,
    filesTotal: 0,
  },

  // Performance metrics
  downloadSpeed: 0, // files/sec, cumulative average since startTime
  estimatedTimeRemaining: 0, // seconds, always a finite number >= 0
  startTime: null,
  lastMetricsAt: null, // last metrics refresh, throttled to METRICS_SAMPLE_INTERVAL
  // Real completions only, reserved slots excluded. filesCompleted counts both, so
  // it still converges to filesTotal, while the speed stays free of the bulk credits.
  realFilesCompleted: 0,

  // Error tracking
  errors: [],
  failedItems: [],

  // Inventory data (stored temporarily during duplication)
  inventoryData: null,

  // Orthanc series IDs of cached series { [orthancSeriesId]: true }
  cachedSeriesUIDs: {},

  // Settings
  concurrency: {
    metadata: 10,
    dicom: 5,
    preview: 20,
  },
};

const orthancDuplicationSlice = createSlice({
  name: 'orthancDuplication',
  initialState,
  reducers: {
    // Set directory access status
    setDirectoryAccess: (state, action) => {
      state.hasDirectoryAccess = action.payload;
    },

    // Set directory name
    setDirectoryName: (state, action) => {
      state.directoryName = action.payload;
    },

    // Set current phase
    setPhase: (state, action) => {
      state.currentPhase = action.payload;
    },

    // Set status
    setStatus: (state, action) => {
      state.status = action.payload;
    },

    // Store inventory data
    setInventoryData: (state, action) => {
      state.inventoryData = action.payload;
    },

    // Mark a series as cached by its Orthanc series ID
    cacheSeriesUID: (state, action) => {
      state.cachedSeriesUIDs[action.payload] = true;
    },

    // Replace cachedSeriesUIDs entirely (used for filesystem restore on mount)
    setCachedSeriesUIDs: (state, action) => {
      const map = {};
      for (const id of action.payload) {
        map[id] = true;
      }
      state.cachedSeriesUIDs = map;
    },

    // The run's total, as reserved by downloadData through the reserve* helpers on
    // the studies it kept. Sole writer of filesTotal, set before the first download.
    setFilteredTotals: (state, action) => {
      state.downloadProgress.filesTotal = action.payload.filesTotal;
    },

    // Add error
    addError: (state, action) => {
      const { studyId, seriesId, instanceId, error, type } = action.payload;

      state.errors.push({
        studyId,
        seriesId,
        instanceId,
        error: error.message || error.toString(),
        type,
        timestamp: Date.now(),
      });

      // Add to failed items
      state.failedItems.push({
        studyId,
        seriesId,
        instanceId,
        type,
        error: error.message || error.toString(),
      });
    },

    // Start the ETA clock — must cover the whole download phase, first study included.
    startTimer: state => {
      state.startTime = Date.now();
    },

    // Calculate download speed and ETA
    updatePerformanceMetrics: state => {
      recomputeMetrics(state);
    },

    // Remove retried items from failedItems and errors
    clearRetriedItems: (state, action) => {
      const keys = new Set(
        action.payload.map(
          i => `${i.type}|${i.studyId}|${i.seriesId}|${i.instanceId}`
        )
      );
      state.failedItems = state.failedItems.filter(
        item =>
          !keys.has(
            `${item.type}|${item.studyId}|${item.seriesId}|${item.instanceId}`
          )
      );
      state.errors = state.errors.filter(
        err =>
          !keys.has(
            `${err.type}|${err.studyId}|${err.seriesId}|${err.instanceId}`
          )
      );
    },

    /**
     * Wipe everything a duplication produced: counters, totals, metrics, errors and
     * the inventory. Used when starting a brand new download from scratch — see
     * resetRunCounters for the retry, which must keep errors and inventory.
     */
    resetProgress: state => {
      state.currentPhase = null;
      state.downloadProgress = {
        filesCompleted: 0,
        filesTotal: 0,
      };
      state.downloadSpeed = 0;
      state.estimatedTimeRemaining = 0;
      state.startTime = null;
      state.lastMetricsAt = null;
      state.realFilesCompleted = 0;
      state.errors = [];
      state.failedItems = [];
      state.inventoryData = null;
    },

    /**
     * Reset only what belongs to a single download run: the completion counters
     * and the ETA clock. Unlike resetProgress, it keeps errors, failedItems and
     * inventoryData, because a retry runs right after clearRetriedItems removed
     * the retried entries only — the other failures must survive.
     * filesTotal is left alone: downloadData rewrites it through setFilteredTotals.
     */
    resetRunCounters: state => {
      state.downloadProgress.filesCompleted = 0;
      state.downloadSpeed = 0;
      state.estimatedTimeRemaining = 0;
      state.startTime = null;
      state.lastMetricsAt = null;
      // Mandatory with the counters above: the previous run's completions would
      // otherwise be averaged over the retry's much shorter elapsed time.
      state.realFilesCompleted = 0;
    },

    /**
     * Apply all accumulated progress increments in a single Immer pass.
     * payload: { fileIncrements: [{reserved}] }
     */
    batchUpdateProgress: (state, action) => {
      const { fileIncrements = [] } = action.payload;

      for (const { reserved } of fileIncrements) {
        state.downloadProgress.filesCompleted++;
        // reserved increments are work that vanished, not work that got done: they
        // must move the bar but never the speed.
        if (!reserved) state.realFilesCompleted++;
      }
    },
  },
});

export const {
  setDirectoryAccess,
  setDirectoryName,
  setPhase,
  setStatus,
  setInventoryData,
  addError,
  startTimer,
  updatePerformanceMetrics,
  resetProgress,
  resetRunCounters,
  batchUpdateProgress,
  clearRetriedItems,
  cacheSeriesUID,
  setCachedSeriesUIDs,
  setFilteredTotals,
} = orthancDuplicationSlice.actions;

export default orthancDuplicationSlice.reducer;

// --- Primitive selectors (stable scalars — no memoization needed) ---
export const selectDuplicationStatus = state => state.orthancDuplication.status;
export const selectDuplicationPhase = state =>
  state.orthancDuplication.currentPhase;
export const selectErrors = state => state.orthancDuplication.errors;
export const selectFailedItems = state => state.orthancDuplication.failedItems;
export const selectDownloadProgress = state =>
  state.orthancDuplication.downloadProgress;

export const selectFilesCompleted = state =>
  state.orthancDuplication.downloadProgress.filesCompleted;
export const selectFilesTotal = state =>
  state.orthancDuplication.downloadProgress.filesTotal;

// --- Memoized compound selectors ---
export const selectOverallProgressPercent = createSelector(
  selectFilesCompleted,
  selectFilesTotal,
  (completed, total) => (total > 0 ? Math.round((completed / total) * 100) : 0)
);

// Memoized despite its single input: the object identity is what keeps useSelector
// from re-rendering the modal on every unrelated action.
export const selectPerformanceMetrics = createSelector(
  state => state.orthancDuplication.estimatedTimeRemaining,
  eta => ({ eta })
);

export const selectCachedSeriesUIDs = state =>
  state.orthancDuplication.cachedSeriesUIDs;
